package com.bulut.mecitogullari.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.snapping.rememberSnapFlingBehavior
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.i18n.fillTemplate
import com.bulut.mecitogullari.ui.theme.AppColors
import kotlin.math.abs
import kotlinx.coroutines.launch

internal val ITEM_HEIGHT = 44.dp
internal const val VISIBLE_ITEMS = 5

/**
 * Selection field. Tapping opens an iOS-style scrollable wheel picker —
 * the same experience on every platform.
 */
@Composable
fun DropdownField(
    label: String,
    value: String,
    options: List<String>,
    onSelect: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    var showPicker by remember { mutableStateOf(false) }

    Box(modifier = modifier) {
        OutlinedTextField(
            value = value,
            onValueChange = {},
            readOnly = true,
            singleLine = true,
            label = { Text(label) },
            trailingIcon = { Text("▾", color = AppColors.TextSecondary) },
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(12.dp),
        )
        Box(
            modifier = Modifier
                .matchParentSize()
                .clickable { showPicker = true },
        )
    }

    if (showPicker) {
        WheelPickerDialog(
            title = label,
            options = options,
            initialValue = value,
            onConfirm = { selected ->
                onSelect(selected)
                showPicker = false
            },
            onDismiss = { showPicker = false },
        )
    }
}

@Composable
fun WheelPickerDialog(
    title: String,
    options: List<String>,
    initialValue: String,
    onConfirm: (String) -> Unit,
    onDismiss: () -> Unit,
) {
    val initialIndex = options.indexOf(initialValue).coerceAtLeast(0)
    var selectedIndex by remember { mutableStateOf(initialIndex) }

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(24.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
        ) {
            Column(modifier = Modifier.padding(top = 20.dp, bottom = 8.dp)) {
                Text(
                    text = title,
                    style = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.padding(horizontal = 24.dp),
                )

                Wheel(
                    options = options,
                    initialIndex = initialIndex,
                    onCenteredIndexChange = { selectedIndex = it },
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 8.dp),
                )

                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp),
                    horizontalArrangement = Arrangement.End,
                ) {
                    TextButton(onClick = onDismiss) { Text(LocalStrings.current.cancel) }
                    TextButton(
                        onClick = {
                            options.getOrNull(selectedIndex)?.let(onConfirm)
                        },
                    ) {
                        Text("Tamam", fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
    }
}

/** Snapping wheel: the row resting at the center is the selection. */
@Composable
internal fun Wheel(
    options: List<String>,
    initialIndex: Int,
    onCenteredIndexChange: (Int) -> Unit,
    modifier: Modifier = Modifier,
    showCenterHighlight: Boolean = true,
) {
    val listState = rememberLazyListState(initialFirstVisibleItemIndex = initialIndex)
    val wheelHeight = ITEM_HEIGHT * VISIBLE_ITEMS
    val verticalPadding = ITEM_HEIGHT * (VISIBLE_ITEMS / 2)

    val centeredIndex by remember {
        derivedStateOf {
            val layoutInfo = listState.layoutInfo
            val viewportCenter = (layoutInfo.viewportStartOffset + layoutInfo.viewportEndOffset) / 2
            layoutInfo.visibleItemsInfo
                .minByOrNull { abs(it.offset + it.size / 2 - viewportCenter) }
                ?.index ?: 0
        }
    }
    LaunchedEffect(centeredIndex) {
        onCenteredIndexChange(centeredIndex)
    }
    val scope = rememberCoroutineScope()

    Box(modifier = modifier.height(wheelHeight)) {
        if (showCenterHighlight) {
            Box(
                modifier = Modifier
                    .align(Alignment.Center)
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp)
                    .height(ITEM_HEIGHT)
                    .background(AppColors.SurfaceMuted, RoundedCornerShape(12.dp)),
            )
        }

        LazyColumn(
            state = listState,
            flingBehavior = rememberSnapFlingBehavior(lazyListState = listState),
            contentPadding = PaddingValues(vertical = verticalPadding),
            modifier = Modifier.fillMaxSize(),
        ) {
            itemsIndexed(options) { index, option ->
                val isCentered = index == centeredIndex
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(ITEM_HEIGHT)
                        .clickable { scope.launch { listState.animateScrollToItem(index) } },
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        text = option,
                        style = if (isCentered) MaterialTheme.typography.titleMedium else MaterialTheme.typography.bodyLarge,
                        color = if (isCentered) AppColors.NavyDeep else AppColors.TextSecondary.copy(alpha = 0.55f),
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.padding(horizontal = 24.dp),
                    )
                }
            }
        }

        // Soft fades at the top and bottom edges
        Box(
            modifier = Modifier
                .align(Alignment.TopCenter)
                .fillMaxWidth()
                .height(ITEM_HEIGHT)
                .background(Brush.verticalGradient(listOf(Color.White, Color.White.copy(alpha = 0f)))),
        )
        Box(
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .fillMaxWidth()
                .height(ITEM_HEIGHT)
                .background(Brush.verticalGradient(listOf(Color.White.copy(alpha = 0f), Color.White))),
        )
    }
}
